<template>
	<v-text-field v-bind="$attrs" type="number" :min="minValue" :max="maxValue" :step="step" :model-value="userInputValue"
		:rules="[
			...(maxValue !== undefined ? [rules.orLess(maxValue)] : []),
			...(minValue !== undefined ? [rules.orMore(minValue)] : []),
			...(decimalLength !== undefined ? [rules.decimalLength(decimalLength)] : []),
			rules.numberFormat,
		]" @update:model-value="onChange" @blur="handleBlur" />
</template>

<script setup lang="ts">
import { nextTick, ref, watch } from "vue";
import rules from "@/utils/validation-rules.js";

defineOptions({ inheritAttrs: false });

type Props = {
	modelValue?: number | string | null;
	minValue?: number;
	maxValue?: number;
	decimalLength?: number;
	step?: number;
};

const props = withDefaults(defineProps<Props>(), {
	step: 0.001,
});

const emit = defineEmits<{
	"update:model-value": [number | undefined | null];
	blur: [];
}>();

const userInputValue = ref<string | number>(props.modelValue ?? "");

watch(
	() => props.modelValue,
	(val) => {
		userInputValue.value = val ?? "";
	},
);

const clampValue = (value: number): number => {
	let clamped = value;
	if (props.minValue !== undefined) {
		clamped = Math.max(props.minValue, clamped);
	}
	if (props.maxValue !== undefined) {
		clamped = Math.min(props.maxValue, clamped);
	}
	return clamped;
};

const truncateDecimals = (str: string): string => {
	if (props.decimalLength === undefined) return str;
	const parts = str.split(".");
	if (parts.length === 2 && parts[1].length > props.decimalLength) {
		return `${parts[0]}.${parts[1].slice(0, props.decimalLength)}`;
	}
	return str;
};

const onChange = (value: number | string | undefined) => {
	const rawValue = value === undefined ? "" : String(value);

	if (rawValue === "") {
		userInputValue.value = "";
		emit("update:model-value", undefined);
		return;
	}

	const correctedStr = truncateDecimals(rawValue);
	const numValue = Number.parseFloat(correctedStr);
	const clampedValue = clampValue(numValue);

	let finalStr = correctedStr;
	if (Number.isFinite(numValue) && numValue !== clampedValue) {
		finalStr = String(clampedValue);
	}

	const needsCorrection = finalStr !== rawValue;

	if (needsCorrection) {
		// Accept the raw value first so Vue detects a prop change,
		// then correct it in nextTick. Both updates resolve within
		// a single animation frame (microtask), so no visible flash.
		userInputValue.value = rawValue;
		nextTick(() => {
			userInputValue.value = finalStr;
			if (Number.isFinite(clampedValue)) {
				emit("update:model-value", clampedValue);
			}
		});
	} else {
		userInputValue.value = finalStr;
		if (Number.isFinite(numValue)) {
			emit("update:model-value", numValue);
		}
	}
};

const handleBlur = () => {
	emit("blur");
};
</script>
